<template>
  <popup-modal ref="popup">
    <h3>{{ title }}</h3>
    <div v-html="message" class="mt-3 mb-4"></div>

    <!-- Opt-in type-to-confirm: the confirm button stays disabled until the exact text is typed. -->
    <div v-if="requireText" class="mb-4">
      <label class="d-block mb-1" style="font-size:0.85rem; color:var(--bo-ink-2)">
        Skriv inn <code>{{ requireText }}</code> for å bekrefte:
      </label>
      <input class="form-control form-control-sm" v-model="typedText" :placeholder="requireText"
             autocomplete="off" @keyup.enter="confirmEnabled && _confirm()" />
    </div>

    <!-- Both buttons size to their label; bo_btn's 120 px floor keeps «Slett» and «Avbryt» even. They
         used to be fixed at 80 px, which that floor turned into exactly 120, so «Ja, merk som utsolgt»
         and every other label past ~12 characters ran out over the button's right edge.
         Cancel is the outline button so the one filled button is the action being confirmed.
         spreadButtons: cancel far left, confirm far right. -->
    <div class="btn-panel" :class="{ 'btn-panel--spread': spreadButtons }">
      <button v-if="showCancelButton" class="bo_btn_secondary" @click="_cancel">{{ cancelButton }}</button>
      <button class="bo_btn" :disabled="!confirmEnabled" @click="_confirm">{{ okButton }}</button>
    </div>
  </popup-modal>
</template>

<script>
  import PopupModal from './PopupModal.vue'

  export default {
    name: 'ConfirmDialogue',

    components: { PopupModal },

    data: () => ({
      // Parameters that change depending on the type of dialogue
      title: undefined,
      message: undefined, // Main text content
      okButton: undefined, // Text for confirm button; leave it empty because we don't know what we're using it for
      cancelButton: 'NOT_SET', // text for cancel button
      spreadButtons: false, // cancel far left / confirm far right, buttons sized to their label
      requireText: '', // opt-in: when set, the user must type this exact string to enable confirm
      typedText: '',

      // Private variables
      resolvePromise: undefined,
      rejectPromise: undefined,
    }),
    computed: {
      showCancelButton(){
          return this.cancelButton !== 'NOT_SET';
      },
      confirmEnabled(){
          return !this.requireText || this.typedText.trim() === this.requireText;
      }
    },
    methods: {
      show(opts = {}) {
        this.title = opts.title
        this.message = opts.message
        this.okButton = opts.okButton
        // Reset, don't retain: the component is a singleton per page, so keeping the previous caller's
        // cancel label meant an info-only dialog inherited an «Avbryt» button from whatever ran before
        // it. Each show() defines its own buttons; 'NOT_SET' is the sentinel that hides cancel.
        this.cancelButton = opts.cancelButton || 'NOT_SET'
        this.requireText = opts.requireText || ''
        this.spreadButtons = !!opts.spreadButtons
        this.typedText = ''
        // Once we set our config, we tell the popup modal to open
        this.$refs.popup.open()
        // Return promise so the caller can get results
        return new Promise((resolve, reject) => {
          this.resolvePromise = resolve
          this.rejectPromise = reject
        })
      },

      _confirm() {
        if (!this.confirmEnabled) return   // guard: type-to-confirm not satisfied (e.g. Enter key)
        this.$refs.popup.close()
        this.resolvePromise(true)
      },

      _cancel() {
        this.$refs.popup.close()
        this.resolvePromise(false)
        // Or you can throw an error
        // this.rejectPromise(new Error('User cancelled the dialogue'))
      },
    },
  }
</script>

<style scoped>
    /* The column gap is the 3rem the confirm button's ms-5 used to give. */
    .btn-panel {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 0.75rem 3rem;
      border-top: var(--bo-muted-2) solid 1px;
      padding-top: 15px;
    }

    .btn-panel--spread {
      justify-content: space-between;
    }

    /* A phone: 3rem pushed even «Avbryt» + «Slett» onto two rows at 320 px. The pair shares the width
       instead, and only two labels too long for one line stack — each then full width. */
    @media (max-width: 575.98px) {
      .btn-panel {
        column-gap: 0.75rem;
      }
      .btn-panel > button {
        flex: 1 1 auto;
      }
    }

    .bo_btn:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
</style>
